import * as func from "../../api/apiHelper";
import * as urls from "../../api/urlHelper";
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { TableRow, convertToIST, formatMstLabel } from "../../utilities";
import {
  faChevronLeft,
  faIdCard,
  faSearchPlus,
  faTimes,
  faFingerprint,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Accordion,
  AccordionContent,
  AccordionPanel,
  AccordionTitle,
} from "flowbite-react";

const TabBtn = ({ active, onClick, icon, children }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-medium border transition-all duration-300 ${
      active
        ? "bg-indigo-600 text-white shadow-md border-indigo-600"
        : "bg-white text-gray-600 border-gray-300 shadow-sm hover:bg-gray-50"
    }`}
  >
    <FontAwesomeIcon icon={icon} className="text-xs" />
    {children}
  </button>
);

const SectionHeader = ({ children }) => (
  <tr className="bg-indigo-50">
    <td colSpan="3" className="text-start px-3 py-2 text-xs font-semibold text-indigo-700 uppercase tracking-wider border border-gray-200">
      {children}
    </td>
  </tr>
);

const ViewBgProfileDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [profileData, setProfileData] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    const fetchProfileDetails = async () => {
      const response = await func.get(urls.GET_BGPROFILE_DETAILS_BY_ID_URL + id);
      if (response.status === 200) setProfileData(response.data.data);
    };
    if (id) fetchProfileDetails();
  }, [id]);

  if (!profileData) {
    return (
      <div className="min-h-screen bg-[linear-gradient(to_left,_#19224B,_#E2B865)] p-6">
        <div className="max-w-5xl mx-auto bg-white rounded-xl shadow-sm p-6 animate-pulse space-y-3">
          {[...Array(10)].map((_, i) => (
            <div key={i} className="h-8 bg-gray-100 rounded-md" />
          ))}
        </div>
      </div>
    );
  }

  const getReadableValue = (obj, key) => (obj && obj[key] ? obj[key] : "N/A");

  const getReadableList = (array, key) => {
    const keyMap = {
      height: "heights", skinTone: "skintype", familyType: "familyType",
      dietType: "dietType", professionSector: "professionSector",
      annualIncome: "annual Income In(₹)", qualification: "qualification",
      star: "zodiacSign", gothram: "gotram", religion: "religion",
      caste: "caste", religiousBelief: "religiousBelief", motherTongue: "motherTongue",
    };
    const objectKey = keyMap[key] || key;
    return array?.length
      ? array.map((item) => item[objectKey]).filter(Boolean).join(",  ")
      : "N/A";
  };

  const getHobbiesNames = () =>
    profileData.selectedHobbies
      ?.map((hobbyId) => (hobbyId ? hobbyId.hisubcategories : null))
      .filter(Boolean);

  const formatPreferenceKey = (key) =>
    key
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (str) => str.toUpperCase())
      .replace("Mother Tongue", "Language");

  // Pretty-print snake_case enum values from the bg_profile model
  // (drinking, smoking, exercise, astrologyBelief, marital sub-fields, etc.)
  const formatEnum = (value) => {
    if (value === null || value === undefined || value === "") return "N/A";
    if (typeof value === "boolean") return value ? "Yes" : "No";
    return String(value)
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  // Astrology belief enum → marketing-readable label (matches mobile copy)
  const formatAstrologyBelief = (v) => {
    switch (v) {
      case "strong_believer": return "Strong believer";
      case "open_believer":   return "Open believer";
      case "curious":         return "Curious, open to it";
      case "opt_out":         return "Not relevant";
      default:                return "N/A";
    }
  };

  // Height: onboarding stores heightFt/heightIn/heightInCm (the `height`
  // master-data ObjectId is usually null for app-created profiles). Prefer the
  // master label when present, else build a readable string from ft/in/cm.
  const formatHeight = (d) => {
    const refLabel = d?.height?.heights;
    if (refLabel) return refLabel;
    const ft = d?.heightFt, inch = d?.heightIn, cm = d?.heightInCm;
    if (ft != null) return `${ft}' ${inch ?? 0}"${cm ? ` · ${cm} cm` : ""}`;
    if (cm != null) return `${cm} cm`;
    return "N/A";
  };

  // A real uploaded image vs the 'pending_upload' placeholder the backend
  // writes on profile create (before the photo-upload step completes).
  const hasRealImage = (url) =>
    typeof url === "string" && url.length > 0 && !url.includes("pending_upload");

  // Birth time (birthHour:birthMinute) — collected for astrology/Manglik.
  const formatBirthTime = (d) => {
    if (d?.birthHour == null && d?.birthMinute == null) return "N/A";
    const h = String(d?.birthHour ?? 0).padStart(2, "0");
    const m = String(d?.birthMinute ?? 0).padStart(2, "0");
    return `${h}:${m}`;
  };

  // Marital status enum → human-readable
  const formatMaritalStatus = (v) => {
    switch (v) {
      case "NEVER_MARRIED":     return "Never married";
      case "PREVIOUSLY_MARRIED": return "Previously married";
      case "WIDOWED":           return "Widowed";
      case "AWAITING_DIVORCE":  return "Awaiting divorce";
      default:                  return "N/A";
    }
  };

  // Photo review status → coloured pill
  const photoStatusPill = (status) => {
    const map = {
      approved: { text: "Approved",  cls: "bg-green-100 text-green-700 border-green-300" },
      pending:  { text: "Pending",   cls: "bg-amber-100 text-amber-700 border-amber-300" },
      rejected: { text: "Rejected",  cls: "bg-red-100 text-red-700 border-red-300" },
    };
    const v = map[status] || { text: status ?? "Unknown", cls: "bg-gray-100 text-gray-700 border-gray-300" };
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-semibold uppercase tracking-wider ${v.cls}`}>
        {v.text}
      </span>
    );
  };

  // Helpers for the second-marriage sub-objects which may be null on
  // never-married profiles. Returns 'N/A' rather than throwing if missing.
  const pm = profileData?.previousMarriageDetails || {};
  const smp = profileData?.secondMarriagePreferences || {};

  // Hide the "My Journey" section entirely for never-married users so admin
  // doesn't see a wall of N/A rows that don't apply.
  const showSecondMarriage =
    profileData?.maritalStatus && profileData.maritalStatus !== "NEVER_MARRIED";

  // ── Build the full set of match-preference rows ───────────────────────────
  // The Preferences subdoc holds two kinds of fields:
  //   • ObjectId arrays (religion, caste, dietType, …) → resolved to labels
  //   • Scalars/string-arrays (age window, height window, location scope, …)
  // The old view rendered only the first kind, so the entire age/height/location
  // part of a user's match preferences was invisible. This flattens both.
  const prefs = profileData?.preferences || {};
  const prefRows = [];
  // ObjectId-array preferences
  Object.entries(prefs)
    .filter(([, value]) => Array.isArray(value) && value.length > 0 && typeof value[0] === "object")
    .forEach(([key, value]) => {
      prefRows.push({
        label: `Preferred ${formatPreferenceKey(key)}`,
        value: getReadableList(value, key === "religiousBelief" ? "belief" : key === "motherTongue" ? "language" : key),
      });
    });
  // Scalar + string-array preferences (age, height, location targeting)
  if (prefs.minAge != null || prefs.maxAge != null)
    prefRows.push({ label: "Preferred Age", value: `${prefs.minAge ?? "Any"} – ${prefs.maxAge ?? "Any"} yrs` });
  if (prefs.minHeightCm != null || prefs.maxHeightCm != null)
    prefRows.push({ label: "Preferred Height", value: `${prefs.minHeightCm ?? "Any"} – ${prefs.maxHeightCm ?? "Any"} cm` });
  if (prefs.locationScope)
    prefRows.push({ label: "Location Scope", value: formatEnum(prefs.locationScope) });
  if (prefs.targetStates?.length)
    prefRows.push({ label: "Target States", value: prefs.targetStates.join(", ") });
  if (prefs.targetCountries?.length)
    prefRows.push({ label: "Target Countries", value: prefs.targetCountries.join(", ") });
  if (prefs.nriPreference && prefs.nriPreference !== "no_preference")
    prefRows.push({ label: "NRI Preference", value: formatEnum(prefs.nriPreference) });
  if (prefs.relocationPreference)
    prefRows.push({ label: "Relocation Note", value: prefs.relocationPreference });
  // Chunk into rows of 3 for the table layout
  const prefRowChunks = prefRows.reduce((rows, cell, i) => {
    if (i % 3 === 0) rows.push([]);
    rows[rows.length - 1].push(cell);
    return rows;
  }, []);

  return (
    <div className="min-h-screen bg-[linear-gradient(to_left,_#19224B,_#E2B865)] p-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="w-8 h-8 flex items-center justify-center bg-white/20 hover:bg-white/30 rounded-lg transition text-white"
            >
              <FontAwesomeIcon icon={faChevronLeft} className="text-sm" />
            </button>
            <div className="flex items-center gap-2">
              <FontAwesomeIcon icon={faIdCard} className="text-white text-lg" />
              <h1 className="text-lg font-semibold text-white">BG Profile Details</h1>
            </div>
          </div>
          {/* Deep Dive into the owning match seeker (keyed by owner userId;
              the overview endpoint resolves seeker by _id or userId). */}
          {(profileData.ownedBy?._id || profileData.ownedBy) && (
            <button
              onClick={() =>
                navigate(`/user-deep-dive/${profileData.ownedBy?._id || profileData.ownedBy}`)
              }
              className="flex items-center gap-2 px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white text-sm font-medium rounded-lg transition"
            >
              <FontAwesomeIcon icon={faFingerprint} className="text-sm" />
              Deep Dive
            </button>
          )}
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6">
          {/* Profile Image + moderation pill */}
          <div className="flex justify-end mb-4">
            <div className="flex flex-col items-end gap-2">
              {hasRealImage(profileData.profileImage) ? (
                <div
                  className="relative w-28 h-28 rounded-full overflow-hidden border-2 border-gray-200 shadow-md group cursor-pointer"
                  onClick={() => setSelectedImage(profileData.profileImage)}
                >
                  <img src={profileData.profileImage} alt="Profile" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <FontAwesomeIcon icon={faSearchPlus} className="text-white text-xl" />
                  </div>
                </div>
              ) : (
                <div className="w-28 h-28 rounded-full border-2 border-dashed border-gray-300 bg-gray-50 flex items-center justify-center">
                  <FontAwesomeIcon icon={faIdCard} className="text-gray-300 text-2xl" />
                </div>
              )}
              {/* Photo moderation status — backend field photoReviewStatus.
                  When no image was ever uploaded (profileImage = 'pending_upload'),
                  show an honest "No photo" pill instead of a false "Approved". */}
              <div className="flex items-center gap-2">
                <span className="text-[10px] text-gray-500 uppercase tracking-wider">Photo</span>
                {hasRealImage(profileData.profileImage)
                  ? photoStatusPill(profileData.photoReviewStatus || "approved")
                  : (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-semibold uppercase tracking-wider bg-red-100 text-red-700 border-red-300">
                      No photo
                    </span>
                  )}
              </div>
            </div>
          </div>

          {/* Gallery */}
          {profileData.imageGallery?.length > 0 && (
            <div className="mb-5">
              <h6 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Image Gallery</h6>
              <div className="grid grid-cols-6 gap-3">
                {profileData.imageGallery.map((image, index) => (
                  <div
                    key={index}
                    className="relative w-28 h-28 rounded-lg overflow-hidden border border-gray-200 shadow group cursor-pointer"
                    onClick={() => setSelectedImage(image)}
                  >
                    <img src={image} alt={`Gallery ${index}`} className="w-full h-full object-contain" />
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <FontAwesomeIcon icon={faSearchPlus} className="text-white text-xl" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Image lightbox */}
          {selectedImage && (
            <div
              className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50"
              onClick={() => setSelectedImage(null)}
            >
              <div className="relative max-w-5xl max-h-[85vh] p-4 flex justify-center" onClick={(e) => e.stopPropagation()}>
                <img
                  src={selectedImage}
                  alt="Selected"
                  className="max-w-[60vw] max-h-[60vh] object-contain rounded-2xl shadow-2xl"
                />
                <button
                  onClick={() => setSelectedImage(null)}
                  className="absolute top-2 right-2 text-white w-8 h-8 flex items-center justify-center rounded-full bg-black/60 hover:bg-black/80 transition-all shadow-lg"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
            </div>
          )}

          {/* Details Table */}
          <div className="overflow-x-auto">
            <table className="w-full border-collapse border border-gray-200 text-sm">
              <tbody>
                <SectionHeader>Personal Details</SectionHeader>
                <TableRow data={[
                  { label: "First Name", value: profileData.firstName || "N/A" },
                  { label: "Last Name", value: profileData.lastName || "N/A" },
                  { label: "Date of Birth", value: convertToIST(profileData.dateOfBirth) || "N/A" },
                ]} />
                <TableRow data={[
                  { label: "Gender", value: profileData.gender || "N/A" },
                  { label: "Mobile Number", value: profileData.contactNumber || "N/A" },
                  { label: "Searching For", value: getReadableValue(profileData.searchingFor, "gender") },
                ]} />
                <TableRow data={[
                  { label: "Birth Location", value: getReadableValue(profileData.birthLocation, "city") },
                ]} />
                <TableRow data={[
                  { label: "Resident Location", value: getReadableValue(profileData.residentLocation, "city") },
                  { label: "Height", value: formatHeight(profileData) },
                  { label: "Weight (Kg)", value: profileData.weight || "N/A" },
                ]} />
                <TableRow data={[
                  { label: "Body Type", value: getReadableValue(profileData.bodyType, "bodytype") },
                  { label: "Skin Tone", value: getReadableValue(profileData.skinTone, "skintype") },
                  { label: "Connected Matchseeker", value: profileData.connectedMatchSeeker?.name || "N/A" },
                ]} />
                <TableRow data={[
                  { label: "Matchseeker Mobile", value: profileData.connectedMatchSeeker?.mobile || "N/A" },
                  // Marital status was previously absent from the admin view; it's
                  // foundational because it gates the My Journey section below.
                  { label: "Marital Status", value: formatMaritalStatus(profileData.maritalStatus) },
                  // Birth city — collected for astrology; stored separately from the
                  // birthLocation ObjectId ref. Resolved to lat/lng by the engine.
                  { label: "Birth City", value: profileData.birthCity || "N/A" },
                ]} />
                <TableRow data={[
                  { label: "Current City", value: profileData.residentCity || "N/A" },
                  { label: "Birth Time", value: formatBirthTime(profileData) },
                  { label: "NRI", value: profileData.isNRI ? `Yes${profileData.countryOfResidence ? ` · ${profileData.countryOfResidence}` : ""}` : "No" },
                ]} />
                <TableRow data={[
                  { label: "Disability", value: profileData.isDisabilityExists ? "Yes" : "No" },
                  { label: "Disability Details", value: profileData.disabilityDetails || "N/A" },
                ]} />

                {/* About / Bio — the 140-char self-description user wrote in
                    onboarding step 4. Renders only when non-empty so we don't
                    show a stub for profiles that skipped it. */}
                {profileData.aboutMe && (
                  <>
                    <SectionHeader>Bio / About</SectionHeader>
                    <tr>
                      <td colSpan="3" className="p-3 border border-gray-200">
                        <p className="text-xs text-gray-500 mb-1">In their own words</p>
                        <p className="text-sm font-medium text-gray-800 italic leading-relaxed whitespace-pre-wrap">
                          “{profileData.aboutMe}”
                        </p>
                      </td>
                    </tr>
                  </>
                )}

                <SectionHeader>Aadhaar Details</SectionHeader>
                <tr>
                  <td colSpan="3">
                    <Accordion className="m-3" collapseAll>
                      <AccordionPanel>
                        <AccordionTitle className="text-black font-bold">
                          Click To View Aadhaar Details
                        </AccordionTitle>
                        <AccordionContent>
                          <table className="w-full border-collapse border border-gray-200 text-left">
                            <tbody>
                              <TableRow data={[
                                { label: "Full Name", value: profileData?._aadharReferenceId?._aadharUserDetails?.full_name ?? "N/A" },
                                { label: "Last 4 Digits", value: profileData?._aadharReferenceId?._last4Digits ?? "N/A" },
                                { label: "DOB", value: profileData?._aadharReferenceId?._aadharUserDetails?.dob ?? "N/A" },
                              ]} />
                              <TableRow data={[
                                { label: "Gender", value: profileData?._aadharReferenceId?._aadharUserDetails?.gender ?? "N/A" },
                                { label: "Care Of", value: profileData?._aadharReferenceId?._aadharUserDetails?.care_of ?? "N/A" },
                                { label: "Country", value: profileData?._aadharReferenceId?._aadharUserDetails?.address?.country ?? "N/A" },
                              ]} />
                              <TableRow data={[
                                { label: "Dist", value: profileData?._aadharReferenceId?._aadharUserDetails?.address?.dist ?? "N/A" },
                                { label: "State", value: profileData?._aadharReferenceId?._aadharUserDetails?.address?.state ?? "N/A" },
                                { label: "Post Office", value: profileData?._aadharReferenceId?._aadharUserDetails?.address?.po ?? "N/A" },
                              ]} />
                              <TableRow data={[
                                { label: "Location", value: profileData?._aadharReferenceId?._aadharUserDetails?.address?.loc ?? "N/A" },
                                { label: "Village/Town/City", value: profileData?._aadharReferenceId?._aadharUserDetails?.address?.vtc ?? "N/A" },
                                { label: "Sub District", value: profileData?._aadharReferenceId?._aadharUserDetails?.address?.subdist ?? "N/A" },
                              ]} />
                              <TableRow data={[
                                { label: "Street", value: profileData?._aadharReferenceId?._aadharUserDetails?.address?.street ?? "N/A" },
                                { label: "House", value: profileData?._aadharReferenceId?._aadharUserDetails?.address?.house ?? "N/A" },
                                { label: "Landmark", value: profileData?._aadharReferenceId?._aadharUserDetails?.address?.landmark ?? "N/A" },
                              ]} />
                              <TableRow data={[
                                { label: "ZIP", value: profileData?._aadharReferenceId?._aadharUserDetails?.address?.zip ?? "N/A" },
                              ]} />
                            </tbody>
                          </table>
                        </AccordionContent>
                      </AccordionPanel>
                    </Accordion>
                  </td>
                </tr>

                <SectionHeader>Community Details</SectionHeader>
                <TableRow data={[
                  { label: "Religion", value: getReadableValue(profileData?.religion, "religion") },
                  { label: "Caste", value: getReadableValue(profileData?.caste, "caste") },
                  { label: "Sub Caste", value: profileData?.subCaste },
                ]} />
                <TableRow data={[
                  { label: "Religious Belief", value: getReadableValue(profileData?.religiousBelief, "belief") },
                  { label: "Mother Tongue", value: getReadableValue(profileData?.motherTongue, "language") },
                ]} />

                <SectionHeader>Education & Professional Details</SectionHeader>
                <TableRow data={[
                  { label: "Qualification", value: getReadableValue(profileData?.qualification, "qualification") },
                  { label: "Field Of Study", value: getReadableValue(profileData?.fieldOfStudy, "profession") },
                  { label: "Profession Sector", value: getReadableValue(profileData?.professionSector, "professionSector") },
                ]} />
                <TableRow data={[
                  { label: "Organisation Name", value: profileData?.organizationName },
                  { label: "Designation", value: getReadableValue(profileData?.professionalDesignation, "designation") },
                  { label: "Annual Income", value: getReadableValue(profileData?.annualIncome, "annual Income In(₹)") },
                ]} />

                <SectionHeader>Astrology Details</SectionHeader>
                <TableRow data={[
                  { label: "Star", value: getReadableValue(profileData?.star, "zodiacSign") },
                  { label: "Nakshatra", value: getReadableValue(profileData?.nakshatra, "nakshatra") },
                  { label: "Raashi", value: getReadableValue(profileData?.raashi, "raashi") },
                ]} />
                <TableRow data={[
                  { label: "Gothram", value: getReadableValue(profileData?.gothram, "gotram") },
                  { label: "Dosha", value: getReadableValue(profileData?.dosha, "dosha") },
                  // Astrology belief drives whether Cosmic Score is included
                  // in matching for this user. Critical signal for support.
                  { label: "Astrology Belief", value: formatAstrologyBelief(profileData?.astrologyBelief) },
                ]} />

                <SectionHeader>Family Details</SectionHeader>
                <TableRow data={[
                  { label: "Family Name", value: profileData?.familyName },
                  { label: "Family Type", value: formatMstLabel(getReadableValue(profileData.familyType, "familyType")) },
                  { label: "Diet Type", value: formatMstLabel(getReadableValue(profileData?.dietType, "dietType")) },
                ]} />
                <TableRow data={[
                  { label: "Mother's Occupation", value: formatMstLabel(getReadableValue(profileData.familyDetails?.motherDetails, "motherDetails")) },
                  { label: "Father's Occupation", value: formatMstLabel(getReadableValue(profileData.familyDetails?.fatherDetails, "fatherDetails")) },
                  { label: "No Of Brothers", value: profileData.familyDetails?.no_of_brothers },
                ]} />
                <TableRow data={[
                  { label: "No Of Sisters", value: profileData.familyDetails?.no_of_sisters },
                ]} />

                {/* Lifestyle — collected during onboarding step 9. Hard filters
                    for matching + part of compatibility scoring. Previously
                    invisible to admin which made support reviews incomplete. */}
                <SectionHeader>Lifestyle & Wellness</SectionHeader>
                <TableRow data={[
                  { label: "Drinking",       value: formatEnum(profileData.drinking) },
                  { label: "Smoking",        value: formatEnum(profileData.smoking) },
                  { label: "Exercise",       value: formatEnum(profileData.exercise) },
                ]} />
                <TableRow data={[
                  { label: "Working Status", value: formatEnum(profileData.workingStatus) },
                ]} />

                {/* My Journey — only relevant for non-NEVER_MARRIED profiles.
                    Renders sub-fields from previousMarriageDetails +
                    secondMarriagePreferences. These are sensitive fields and
                    admins need full visibility for empathetic support. */}
                {showSecondMarriage && (
                  <>
                    <SectionHeader>My Journey (Second Marriage)</SectionHeader>
                    <TableRow data={[
                      { label: "Has Children",      value: formatEnum(pm.hasChildren) },
                      { label: "Children Count",    value: pm.childrenCount ?? "N/A" },
                      { label: "Legal Status",      value: formatEnum(pm.legalStatus) },
                    ]} />
                    <TableRow data={[
                      { label: "Separation Duration",   value: formatEnum(pm.separationDuration) },
                      { label: "Current Living",        value: formatEnum(pm.currentLivingSituation) },
                      { label: "Children Living With",  value: formatEnum(pm.childrenLivingWith) },
                    ]} />
                    <TableRow data={[
                      { label: "Co-parenting Status",        value: formatEnum(pm.coParentingStatus) },
                      { label: "Financial Obligations",      value: formatEnum(pm.hasFinancialObligations) },
                      { label: "Relocation Constraints",     value: formatEnum(pm.hasRelocationConstraints) },
                    ]} />
                    <TableRow data={[
                      { label: "Family Acceptance",          value: formatEnum(pm.familyAcceptance) },
                      { label: "Children Age Ranges",
                        value: (pm.childrenAgeRanges?.length ? pm.childrenAgeRanges.join(", ") : "N/A") },
                    ]} />
                    {/* Partner preferences for second marriage */}
                    <tr className="bg-indigo-50/60">
                      <td colSpan="3" className="text-start px-3 py-1.5 text-[11px] font-semibold text-indigo-600 uppercase tracking-wider border border-gray-200">
                        Partner Preferences (Second Marriage)
                      </td>
                    </tr>
                    <TableRow data={[
                      { label: "Open To Partner With Children", value: formatEnum(smp.openToPartnerWithChildren) },
                      { label: "Open To Previously Married",    value: formatEnum(smp.openToPreviouslyMarried) },
                      { label: "Family Acceptance Matters",     value: formatEnum(smp.partnerFamilyAcceptanceMatters) },
                    ]} />
                    <TableRow data={[
                      { label: "Open To Partner Legal Statuses",
                        value: (smp.openToPartnerLegalStatuses?.length
                          ? smp.openToPartnerLegalStatuses.map(formatEnum).join(", ")
                          : "N/A") },
                    ]} />
                  </>
                )}

                <SectionHeader>Preferences</SectionHeader>
                {prefRowChunks.length > 0
                  ? prefRowChunks.map((row, rowIndex) => <TableRow key={rowIndex} data={row} />)
                  : <TableRow data={[{ label: "Preferences", value: "No Preferences Set" }]} />}

                <SectionHeader>Hobbies & Interests</SectionHeader>
                {getHobbiesNames().length > 0
                  ? getHobbiesNames()
                      .reduce((rows, hobby, index) => {
                        if (index % 3 === 0) rows.push([]);
                        rows[rows.length - 1].push(hobby);
                        return rows;
                      }, [])
                      .map((row, rowIndex) => (
                        <TableRow key={rowIndex} data={row.map((hobby) => ({ label: "Hobby", value: hobby }))} />
                      ))
                  : <TableRow data={[{ label: "Hobby", value: "N/A" }]} />}
              </tbody>
            </table>
          </div>

        </div>
      </div>
    </div>
  );
};

export default ViewBgProfileDetails;
